// ============================================================================
// SocialLinks - the official PokerSweepStars accounts
// ============================================================================
// One place that owns the handles, so a rename never leaves a dead link
// somewhere in the app. Drawn as real brand glyphs rather than emoji, and
// muted by default so they read as a footer rather than a call to action.
//
// Opening: plain window.open with noopener. On the web that is a new tab; in
// the iOS/Android shell Capacitor hands http(s) links with target=_blank to
// the system browser, which is what you want for an external link - it keeps
// the app running underneath instead of navigating away from it.
// ============================================================================
import React from 'react';
import { HStack, Link, Tooltip, Box } from '@chakra-ui/react';
import { Instagram, Youtube } from 'lucide-react';

export interface SocialAccount {
  label: string;
  handle: string;
  url: string;
  icon: React.ReactNode;
}

// X and TikTok have no lucide glyph (and lucide's "Twitter" is the retired
// bird), so both are drawn here to stay on-brand.
const XGlyph = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokGlyph = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82s.51.5 0 0A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z" />
  </svg>
);

export const SOCIAL_ACCOUNTS: SocialAccount[] = [
  {
    label: 'Instagram',
    handle: '@poker_sweepstars',
    url: 'https://www.instagram.com/poker_sweepstars/',
    icon: <Instagram size={17} />,
  },
  {
    label: 'TikTok',
    handle: '@pokersweepstarsusa',
    url: 'https://www.tiktok.com/@pokersweepstarsusa',
    icon: <TikTokGlyph />,
  },
  {
    label: 'X',
    handle: '@PokerSweep',
    url: 'https://x.com/PokerSweep',
    icon: <XGlyph />,
  },
  {
    label: 'YouTube',
    handle: 'PokerSweepStars',
    url: 'https://www.youtube.com/@PokerSweepStars',
    icon: <Youtube size={18} />,
  },
];

interface SocialLinksProps {
  /** Icon colour when idle. Defaults to a muted grey. */
  color?: string;
  /** Colour on hover/focus. */
  hoverColor?: string;
  size?: 'sm' | 'md';
  justify?: 'center' | 'flex-start';
}

const SocialLinks: React.FC<SocialLinksProps> = ({
  color = 'gray.500',
  hoverColor = '#d4af37',
  size = 'md',
  justify = 'center',
}) => {
  const box = size === 'sm' ? 8 : 9;

  return (
    <HStack spacing={size === 'sm' ? 1 : 2} justify={justify}>
      {SOCIAL_ACCOUNTS.map(acct => (
        <Tooltip
          key={acct.label}
          label={`${acct.label} · ${acct.handle}`}
          fontSize="xs"
          openDelay={300}
        >
          <Link
            href={acct.url}
            isExternal
            aria-label={`${acct.label} - ${acct.handle}`}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w={box}
            h={box}
            borderRadius="md"
            color={color}
            transition="color 0.15s ease, background 0.15s ease"
            _hover={{ color: hoverColor, bg: 'whiteAlpha.100' }}
            _focusVisible={{ color: hoverColor, boxShadow: `0 0 0 2px ${hoverColor}` }}
          >
            <Box as="span" display="flex">{acct.icon}</Box>
          </Link>
        </Tooltip>
      ))}
    </HStack>
  );
};

export default SocialLinks;
